import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Post, User, Poll, CreateSpaceRequest } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { likePost, unlikePost, sharePost, getUserById, votePoll, voteMultiplePoll, subscribeToPost, deletePost } from '../../services/firebaseService';
import { isGifUrl } from '../../utils/gifUtils';
import { MediaService } from '../../services/mediaService';
import LinkEnhancedContent from '../UI/LinkEnhancedContent';
import SwipeablePostCard from '../UI/SwipeablePostCard';
import MediaDisplay from '../UI/MediaDisplay';
import { usePostSpaces } from '../../hooks/useSpaces';
import { usePostView } from '../../hooks/usePostView';
import { formatViewCount } from '../../utils/formatUtils';
import SpaceCardWidget from '../Spaces/SpaceCardWidget';
import SpaceDropdownMenu from '../Spaces/SpaceDropdownMenu';

interface PostCardProps {
  post: Post;
  onLike?: (postId: string) => void;
  onComment?: (postId: string, content: string) => void;
  onAvatarClick?: (userId: string) => void;
  onPostClick?: (post: Post) => void;
  onDelete?: (postId: string) => void;
}

const PostCard: React.FC<PostCardProps> = ({ post: initialPost, onLike, onComment, onAvatarClick, onPostClick, onDelete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState<Post>(initialPost);
  const [author, setAuthor] = useState<User | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes.length);
  const [sharesCount, setSharesCount] = useState(post.shares);
  const [isLiking, setIsLiking] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedPollOptions, setSelectedPollOptions] = useState<string[]>([]);
  const [hasVoted, setHasVoted] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  const [showCreateSpaceDropdown, setShowCreateSpaceDropdown] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Utilisation du hook pour les Spaces
  const { 
    spaces, 
    loading: spacesLoading, 
    createSpace, 
    joinSpace,
    isUserInSpace 
  } = usePostSpaces(post.id);

  // Gérer intelligemment les vues du post
  usePostView(post.id, user?.id);

  // Empêcher le défilement du body quand les modals sont ouverts
  useEffect(() => {
    if (showProfileModal || selectedImageIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showProfileModal, selectedImageIndex]);

  // Fermer le menu d'options en cliquant ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showOptionsMenu) {
        setShowOptionsMenu(false);
      }
    };

    if (showOptionsMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showOptionsMenu]);

  const handleJoinSpace = async (spaceId: string) => {
    try {
      await joinSpace(spaceId);
      // Navigation vers la vue du Space
      console.log('Navigation vers Space:', spaceId);
      navigate(`/spaces/${spaceId}`);
    } catch (error) {
      console.error('Erreur rejoindre Space:', error);
    }
  };

  const handleCreateSpace = async (spaceData: CreateSpaceRequest): Promise<string | null> => {
    try {
      const spaceId = await createSpace(spaceData);
      if (spaceId) {
        setShowCreateSpaceDropdown(false);
        // Navigation vers le nouveau Space
        console.log('Navigation vers nouveau Space:', spaceId);
        navigate(`/spaces/${spaceId}`);
        return spaceId;
      }
      return null;
    } catch (error) {
      console.error('Erreur création Space:', error);
      return null;
    }
  };

  const categoryEmojis = {
    general: '🌟',
    feedback: '💬',
    meme: '😂',
    research: '🔬',
    launchpad: '🚀',
    dao: '🏛️',
    annonces: '📢',
  };

  useEffect(() => {
    // Écouter les changements du post en temps réel
    const unsubscribe = subscribeToPost(post.id, (updatedPost) => {
      if (updatedPost) {
        setPost(updatedPost);
      }
    });

    // Nettoyer l'abonnement au démontage
    return () => unsubscribe();
  }, [post.id]);

  useEffect(() => {
    // Réinitialiser et vérifier les états quand le post ou l'utilisateur change
    setIsLiked(false);
    setHasVoted(false);
    setSelectedPollOptions([]);

    // Vérifier si l'utilisateur a liké ce post
    if (user && post.likes.includes(user.id)) {
      setIsLiked(true);
    }

    // Vérifier si l'utilisateur a voté dans le sondage
    if (user && post.poll && post.poll.voters.includes(user.id)) {
      setHasVoted(true);
      // Trouver les options pour lesquelles l'utilisateur a voté
      const userVotes = post.poll.options
        .filter(option => option.votes.includes(user.id))
        .map(option => option.id);
      setSelectedPollOptions(userVotes);
    }

    // Mettre à jour le nombre de likes
    setLikesCount(post.likes.length);

    // ...existing code...

    // Vérifier si l'utilisateur a voté dans le sondage
    if (user && post.poll && post.poll.voters.includes(user.id)) {
      setHasVoted(true);
      // Trouver les options pour lesquelles l'utilisateur a voté
      const userVotes = post.poll.options
        .filter(option => option.votes.includes(user.id))
        .map(option => option.id);
      setSelectedPollOptions(userVotes);
    }

    // Charger les vraies données de l'auteur depuis Firestore
    const loadAuthor = async () => {
      if (post.authorId) {
        try {
          const authorData = await getUserById(post.authorId);
          if (authorData) {
            setAuthor(authorData);
          } else {
            // Fallback avec des données minimales si l'utilisateur n'est pas trouvé
            setAuthor({
              id: post.authorId,
              walletAddress: '',
              username: `user_${post.authorId.slice(-8)}`,
              displayName: `Utilisateur ${post.authorId.slice(-4)}`,
              bio: '',
              xp: 0,
              level: 1,
              badges: [],
              socialLinks: {},
              isVerified: false,
              role: 'user',
              following: [],
              followers: [],
              followersCount: 0,
              followingCount: 0,
              profilePicture: `https://api.dicebear.com/7.x/shapes/svg?seed=${post.authorId}`,
              createdAt: new Date(),
              updatedAt: new Date()
            });
          }
        } catch (error) {
          console.error('Erreur chargement auteur:', error);
          // Fallback en cas d'erreur
          setAuthor({
            id: post.authorId,
            walletAddress: '',
            username: `user_${post.authorId.slice(-8)}`,
            displayName: `Utilisateur ${post.authorId.slice(-4)}`,
            bio: '',
            xp: 0,
            level: 1,
            badges: [],
            socialLinks: {},
            isVerified: false,
            role: 'user',
            following: [],
            followers: [],
            followersCount: 0,
            followingCount: 0,
            createdAt: new Date(),
            updatedAt: new Date()
          });
        }
      }
    };

    loadAuthor();
  }, [user, post.likes, post.authorId]);

  const handleLike = async () => {
    if (!user || isLiking) return;

    setIsLiking(true);
    const originalIsLiked = isLiked;
    const originalCount = likesCount;

    try {
      // État optimiste
      if (isLiked) {
        setIsLiked(false);
        setLikesCount(prev => prev - 1);
        await unlikePost(post.id, user.id);
      } else {
        setIsLiked(true);
        setLikesCount(prev => prev + 1);
        await likePost(post.id, user.id);
      }
      onLike?.(post.id);
    } catch (error) {
      console.error('Erreur lors du like:', error);
      // Restaurer l'état en cas d'erreur
      setIsLiked(originalIsLiked);
      setLikesCount(originalCount);
      
      // Afficher une notification d'erreur si possible
      if (error instanceof Error && error.message.includes('permissions')) {
        console.warn('Problème de permissions - utilisateur peut-être non connecté');
      }
    } finally {
      setIsLiking(false);
    }
  };

  const handleShare = async () => {
    const postUrl = `${window.location.origin}/post/${post.id}`;
    
    try {
      // Incrémenter le compteur de partages
      await sharePost(post.id);
      setSharesCount(prev => prev + 1);
      
      if (navigator.share) {
        // Utiliser l'API native de partage si disponible
        try {
          await navigator.share({
            title: 'Jupipotes - Post',
            text: post.content.substring(0, 100) + (post.content.length > 100 ? '...' : ''),
            url: postUrl,
          });
        } catch (error) {
          console.log('Partage annulé');
        }
      } else {
        // Fallback : copier le lien dans le presse-papiers
        try {
          await navigator.clipboard.writeText(postUrl);
          // Afficher une notification temporaire
          const notification = document.createElement('div');
          notification.className = 'fixed top-4 right-4 bg-emerald-700 text-emerald-100 px-4 py-2 rounded-lg shadow-lg z-50 cosmic-card';
          notification.textContent = 'Lien copié dans le presse-papiers !';
          document.body.appendChild(notification);
          
          setTimeout(() => {
            document.body.removeChild(notification);
          }, 3000);
        } catch (error) {
          console.error('Erreur lors de la copie du lien:', error);
        }
      }
    } catch (error) {
      console.error('Erreur lors du partage:', error);
    }
  };

  const handleDelete = async () => {
    if (!user || post.authorId !== user.id) return;
    
    const confirmed = window.confirm('Êtes-vous sûr de vouloir supprimer ce post ? Cette action est irréversible.');
    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await deletePost(post.id);
      onDelete?.(post.id);
      
      // Notification de succès
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-emerald-700 text-emerald-100 px-4 py-2 rounded-lg shadow-lg z-50 cosmic-card';
      notification.textContent = 'Post supprimé avec succès !';
      document.body.appendChild(notification);
      
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 3000);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du post');
    } finally {
      setIsDeleting(false);
      setShowOptionsMenu(false);
    }
  };

  const formatTimeAgo = (date: Date | any) => {
    const now = new Date();
    
    // Convertir Timestamp Firebase en Date si nécessaire
    let targetDate: Date;
    if (date && typeof date.toDate === 'function') {
      // C'est un Timestamp Firebase
      targetDate = date.toDate();
    } else if (date instanceof Date) {
      // C'est déjà un objet Date
      targetDate = date;
    } else {
      // Fallback vers maintenant
      targetDate = new Date();
    }
    
    const diffInSeconds = Math.floor((now.getTime() - targetDate.getTime()) / 1000);

    if (diffInSeconds < 60) return 'À l\'instant';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    return `${Math.floor(diffInSeconds / 86400)}j`;
  };

  const renderImages = () => {
    if (!post.mediaUrls || post.mediaUrls.length === 0) return null;
    const imageCount = post.mediaUrls.length;

    // Vérifier si c'est un GIF (une seule URL qui est un GIF)
    if (imageCount === 1 && isGifUrl(post.mediaUrls[0])) {
      return (
        <div className="mt-3 rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-cosmic-neon flex items-center justify-center" style={{ maxHeight: '400px' }}>
          <img
            src={post.mediaUrls[0]}
            alt="GIF"
            className="max-w-full max-h-full object-contain rounded-xl cursor-pointer hover:scale-105 transition-transform duration-300"
            onClick={() => setSelectedImageIndex(0)}
          />
        </div>
      );
    }

    // Harmonisation : grille responsive, glassmorphism, ratio carré, hover zoom
    if (imageCount === 1) {
      return (
        <div className="mt-3 rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-cosmic-neon flex items-center justify-center" style={{ maxHeight: '500px' }}>
          <img
            src={post.mediaUrls[0]}
            alt="Post media"
            className="max-w-full max-h-[500px] w-auto h-auto object-contain rounded-xl transition-transform duration-300 hover:scale-105 cursor-pointer bg-black"
            onClick={() => setSelectedImageIndex(0)}
            style={{ display: 'block', margin: '0 auto' }}
          />
        </div>
      );
    }
    if (imageCount === 2) {
      return (
        <div className="mt-3 grid grid-cols-2 gap-3">
          {post.mediaUrls.map((url, index) => (
            <div key={index} className="rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-cosmic-neon flex items-center justify-center" style={{ maxHeight: '500px' }}>
              <img
                src={url}
                alt={`Post media ${index + 1}`}
                className="max-w-full max-h-[500px] w-auto h-auto object-contain rounded-xl transition-transform duration-300 hover:scale-105 cursor-pointer bg-black"
                onClick={() => setSelectedImageIndex(index)}
                style={{ display: 'block', margin: '0 auto' }}
              />
            </div>
          ))}
        </div>
      );
    }
    if (imageCount === 3) {
      return (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-cosmic-neon flex items-center justify-center" style={{ maxHeight: '500px' }}>
            <img
              src={post.mediaUrls[0]}
              alt="Post media 1"
              className="max-w-full max-h-[500px] w-auto h-auto object-contain rounded-xl transition-transform duration-300 hover:scale-105 cursor-pointer bg-black"
              onClick={() => setSelectedImageIndex(0)}
              style={{ display: 'block', margin: '0 auto' }}
            />
          </div>
          <div className="grid grid-rows-2 gap-3">
            {post.mediaUrls.slice(1).map((url, index) => (
              <div key={index + 1} className="rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-cosmic-neon flex items-center justify-center" style={{ maxHeight: '500px' }}>
                <img
                  src={url}
                  alt={`Post media ${index + 2}`}
                  className="max-w-full max-h-[500px] w-auto h-auto object-contain rounded-xl transition-transform duration-300 hover:scale-105 cursor-pointer bg-black"
                  onClick={() => setSelectedImageIndex(index + 1)}
                  style={{ display: 'block', margin: '0 auto' }}
                />
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (imageCount >= 4) {
      return (
        <div className="mt-3 grid grid-cols-2 gap-3">
          {post.mediaUrls.slice(0, 3).map((url, index) => (
            <div key={index} className="rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-cosmic-neon flex items-center justify-center" style={{ maxHeight: '500px' }}>
              <img
                src={url}
                alt={`Post media ${index + 1}`}
                className="max-w-full max-h-[500px] w-auto h-auto object-contain rounded-xl transition-transform duration-300 hover:scale-105 cursor-pointer bg-black"
                onClick={() => setSelectedImageIndex(index)}
                style={{ display: 'block', margin: '0 auto' }}
              />
            </div>
          ))}
          <div
            className="relative rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-cosmic-neon cursor-pointer flex items-center justify-center"
            onClick={() => setSelectedImageIndex(3)}
            style={{ aspectRatio: '1/1', maxHeight: '500px' }}
          >
            <img
              src={post.mediaUrls[3]}
              alt="Post media 4"
              className="max-w-full max-h-[500px] w-auto h-auto object-contain rounded-xl opacity-60 bg-black"
              style={{ display: 'block', margin: '0 auto' }}
            />
            {imageCount > 4 && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-xl flex items-center justify-center">
                <span className="text-white text-xl font-bold">+{imageCount - 3}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
  };

      const ProfileModal = () => {
    if (!showProfileModal || !author) return null;

    return (
      <div 
        className="fixed inset-0 bg-gradient-to-br from-emerald-900/95 via-gray-900/95 to-cyan-900/95 backdrop-blur-lg flex items-center justify-center z-50 p-4"
        onClick={() => setShowProfileModal(false)}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      >
        {/* Effet de particules flottantes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400/60 rounded-full animate-float pointer-events-none"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        {/* Modal content */}
        <div 
          className="relative z-10 w-full max-w-md mx-4 cosmic-card p-8 transform transition-all duration-500 animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setShowProfileModal(false)}
            className="absolute top-4 right-4 text-emerald-300/60 hover:text-emerald-100 transition-colors p-1"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* En-tête du profil */}
          <div className="flex flex-col items-center mb-6">
            <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border-4 border-emerald-700/50">
              <img 
                src={author?.profilePicture || `https://api.dicebear.com/7.x/shapes/svg?seed=${author.id}`} 
                alt={author?.displayName || 'Utilisateur'}
                className="w-full h-full object-cover"
              />
            </div>
            <h2 className="text-2xl font-bold text-emerald-100">
              {author.displayName}
              {author.isVerified && (
                <span className="inline-block ml-2 text-cyan-400" title="Compte vérifié">
                  <svg className="w-5 h-5 inline" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
            </h2>
            <p className="text-emerald-300/80">@{author.username || 'jupiter_user'}</p>
          </div>

          {/* Informations du profil */}
          <div className="space-y-4">
            {author.bio && (
              <div className="text-emerald-100 mb-4">
                <p className="whitespace-pre-wrap">{author.bio}</p>
              </div>
            )}

            <div className="flex justify-center space-x-8 border-t border-b border-emerald-800/50 py-4">
              <div className="text-center">
                <div className="text-xl font-semibold text-emerald-100">{author.followersCount}</div>
                <div className="text-sm text-emerald-300/80">Abonnés</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-emerald-100">{author.followingCount}</div>
                <div className="text-sm text-emerald-300/80">Abonnements</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-emerald-100">{author.xp}</div>
                <div className="text-sm text-emerald-300/80">XP</div>
              </div>
            </div>

            {/* Niveau et badges */}
            <div className="flex justify-between items-center">
              <div className="text-emerald-100">
                <span className="text-emerald-300/80 mr-2">Niveau:</span>
                <span className="font-semibold">{author.level}</span>
              </div>
              {author.badges && author.badges.length > 0 && (
                <div className="flex items-center space-x-1">
                  {author.badges.slice(0, 3).map((badge, index) => (
                    <div key={index} className="w-6 h-6 rounded-full bg-emerald-700/50 flex items-center justify-center" title={typeof badge === 'string' ? badge : ''}>
                      {'🏅'}
                    </div>
                  ))}
                  {author.badges.length > 3 && (
                    <div className="text-xs text-emerald-300/80">+{author.badges.length - 3}</div>
                  )}
                </div>
              )}
            </div>

            {/* Bouton d'action */}
            <button 
              className="w-full py-2 mt-4 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-emerald-100 font-medium transition-colors cosmic-hover-effect"
              onClick={() => {
                setShowProfileModal(false);
                // Navigation sans rechargement via React Router
                navigate(`/profile/${author.id}`);
              }}
            >
              Voir le profil complet
            </button>
          </div>
        </div>
      </div>
    );
      };

  const ImageModal = () => {
    if (selectedImageIndex === null || !post.mediaUrls) return null;

    const modalContent = (
      <div 
        className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-2 sm:p-4"
        onClick={() => setSelectedImageIndex(null)}
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          zIndex: 9999,
          margin: 0,
          transform: 'none'
        }}
      >
        <div className="relative w-full h-full max-w-7xl max-h-full flex items-center justify-center">
          <img
            src={post.mediaUrls[selectedImageIndex]}
            alt={`Post media ${selectedImageIndex + 1}`}
            className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '95vw', maxHeight: '95vh' }}
          />
          
          {/* Bouton fermer - Mobile responsive */}
          <button
            onClick={() => setSelectedImageIndex(null)}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white bg-black bg-opacity-60 hover:bg-opacity-80 rounded-full w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center transition-all duration-200 z-10"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Compteur d'images */}
          {post.mediaUrls.length > 1 && (
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
              {selectedImageIndex + 1} / {post.mediaUrls.length}
            </div>
          )}
          
          {/* Boutons de navigation - Mobile responsive */}
          {post.mediaUrls.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(prev => 
                    prev === null ? 0 : prev > 0 ? prev - 1 : post.mediaUrls!.length - 1
                  );
                }}
                className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-60 hover:bg-opacity-80 rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center transition-all duration-200 z-10"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(prev => 
                    prev === null ? 0 : prev < post.mediaUrls!.length - 1 ? prev + 1 : 0
                  );
                }}
                className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-60 hover:bg-opacity-80 rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center transition-all duration-200 z-10"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
    );

    // Utiliser un Portal pour rendre le modal dans le body, en dehors de la hiérarchie du layout
    return createPortal(modalContent, document.body);
  };

  const handlePollVote = async (optionId: string) => {
    if (!user || !post.poll || hasVoted || isVoting) return;

    setIsVoting(true);
    try {
      if (post.poll.allowMultipleChoice) {
        // Pour les choix multiples, on met juste à jour la sélection locale
        if (selectedPollOptions.includes(optionId)) {
          setSelectedPollOptions(prev => prev.filter(id => id !== optionId));
        } else {
          setSelectedPollOptions(prev => [...prev, optionId]);
        }
      } else {
        // Pour choix unique, on vote immédiatement
        await votePoll(post.id, optionId, user.id);
        setSelectedPollOptions([optionId]);
        setHasVoted(true);
      }
      
    } catch (error) {
      console.error('Erreur lors du vote:', error);
      alert('Erreur lors du vote: ' + (error as Error).message);
    } finally {
      setIsVoting(false);
    }
  };

  const submitPollVote = async () => {
    if (!user || !post.poll || selectedPollOptions.length === 0 || isVoting) return;

    setIsVoting(true);
    try {
      if (post.poll.allowMultipleChoice) {
        await voteMultiplePoll(post.id, selectedPollOptions, user.id);
      } else {
        await votePoll(post.id, selectedPollOptions[0], user.id);
      }
      setHasVoted(true);
      
    } catch (error) {
      console.error('Erreur lors de la soumission du vote:', error);
      alert('Erreur lors du vote: ' + (error as Error).message);
    } finally {
      setIsVoting(false);
    }
  };

  if (!author) {
    return (
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 animate-pulse">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-gray-700 rounded-full"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-700 rounded w-24 mb-2"></div>
            <div className="h-3 bg-gray-700 rounded w-16"></div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-700 rounded"></div>
          <div className="h-4 bg-gray-700 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {showProfileModal && <ProfileModal />}
      <SwipeablePostCard
        post={post}
        user={user}
        onLike={handleLike}
        onReply={() => onPostClick?.(post)}
        onShare={handleShare}
        onBookmark={() => console.log('Bookmark functionality not implemented yet')}
        onReport={() => console.log('Report functionality not implemented yet')}
        onMute={() => console.log('Mute functionality not implemented yet')}
        onHide={() => console.log('Hide functionality not implemented yet')}
      >
        <div className="cosmic-card p-4 lg:p-6 cosmic-hover-effect">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 lg:mb-4">
          <div className="flex items-center space-x-2 lg:space-x-3">
            <div
              className="w-8 h-8 lg:w-10 lg:h-10 rounded-full overflow-hidden bg-gradient-to-r from-emerald-700 to-cyan-600 cursor-pointer border border-cyan-400/30 animate-pulse btn-touch"
              onClick={(e) => {
                e.stopPropagation();
                setShowProfileModal(true);
                // Ne pas appeler onAvatarClick pour éviter l'ouverture de deux modals
              }}
            >
              <img 
                src={author?.profilePicture || `https://api.dicebear.com/7.x/shapes/svg?seed=${post.authorId}`} 
                alt={author?.displayName || 'Utilisateur'}
                className="w-full h-full object-cover"
              />
            </div>
              <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-emerald-100 text-sm lg:text-base truncate">{author.displayName}</span>
                {author.isVerified && (
                  <svg className="w-3 h-3 lg:w-4 lg:h-4 text-cyan-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="flex items-center space-x-2 text-sm text-emerald-300/80">
                <span>@{author.username || 'jupiter_user'}</span>
                <span>•</span>
                <span>{formatTimeAgo(post.createdAt || new Date())}</span>
                <span>•</span>
                <span className="flex items-center space-x-1 bg-emerald-900/30 px-2 py-1 rounded-full">
                  <span className="text-xs">{categoryEmojis[post.category]}</span>
                  <span className="text-xs capitalize font-medium">{post.category}</span>
                </span>
                {/* Badge Thread */}
                {post.isThread && (
                  <>
                    <span>•</span>
                    <span className="flex items-center space-x-1 bg-cyan-900/40 border border-cyan-500/30 px-2 py-1 rounded-full">
                      <svg className="w-3 h-3 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-xs font-medium text-cyan-300">
                        {post.threadLength || 1} {(post.threadLength || 1) > 1 ? 'parties' : 'partie'}
                      </span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Options Menu - Only for post author */}
          {user && post.authorId === user.id && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOptionsMenu(!showOptionsMenu);
                }}
                className="p-1 rounded-full text-emerald-300/60 hover:text-emerald-300 hover:bg-emerald-900/30 transition-colors"
                disabled={isDeleting}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01" />
                </svg>
              </button>
              
              {showOptionsMenu && (
                <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-emerald-700/30 rounded-lg shadow-lg z-50 min-w-[120px]">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete();
                    }}
                    disabled={isDeleting}
                    className="w-full px-3 py-2 text-left text-red-400 hover:bg-red-900/20 transition-colors flex items-center space-x-2 disabled:opacity-50"
                  >
                    {isDeleting ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                    <span className="text-sm">{isDeleting ? 'Suppression...' : 'Supprimer'}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content with Enhanced Links */}
        <div 
          className="mb-4 cursor-pointer"
          onClick={() => onPostClick?.(post)}
        >
          <LinkEnhancedContent content={
            post.content || 
            // Fallback pour les anciens threads mal formatés
            (post.isThread && post.threadParts && post.threadParts.length > 0 ? post.threadParts[0].content : '')
          } />
          {/* Pour les threads, afficher un aperçu des parties suivantes */}
          {post.isThread && post.threadParts && post.threadParts.length > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-700/30">
              <div className="text-emerald-300/80 text-sm mb-2 flex items-center space-x-1">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Suite du thread...</span>
              </div>
              <div className="text-emerald-200/70 text-sm line-clamp-2">
                {/* Afficher la partie suivante : si post.content est vide, montrer threadParts[1], sinon threadParts[0] */}
                {post.content ? 
                  (post.threadParts[0]?.content || '') : 
                  (post.threadParts[1]?.content || post.threadParts[0]?.content || '')
                }
              </div>
            </div>
          )}
        </div>

        {/* Poll */}
        {post.poll && (
          <div className="mb-4 p-4 bg-emerald-900/20 border border-emerald-700/30 rounded-xl">
            <h4 className="font-medium text-emerald-100 mb-3">{post.poll.question}</h4>
            
            <div className="space-y-2">
              {post.poll.options.map((option) => {
                // Calculer dynamiquement le total des votes
                const totalVotes = post.poll!.options.reduce((sum, opt) => sum + opt.votes.length, 0);
                const votePercentage = totalVotes > 0 
                  ? (option.votes.length / totalVotes) * 100 
                  : 0;
                const isSelected = selectedPollOptions.includes(option.id);
                const userVoted = hasVoted && option.votes.includes(user?.id || '');

                return (
                  <div key={option.id} className="relative">
                    {hasVoted ? (
                      // Affichage des résultats
                      <div className="relative p-3 bg-gray-800/50 border border-emerald-800/30 rounded-lg overflow-hidden">
                        <div 
                          className="absolute inset-0 bg-emerald-600/20 transition-all duration-500"
                          style={{ width: `${votePercentage}%` }}
                        />
                        <div className="relative flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {userVoted && (
                              <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                            )}
                            <span className="text-sm text-emerald-200">{option.text}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-emerald-300/60">{option.votes.length}</span>
                            <span className="text-xs text-emerald-300/60">{votePercentage.toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Interface de vote
                      <button
                        onClick={() => handlePollVote(option.id)}
                        disabled={isVoting}
                        className={`w-full p-3 text-left border rounded-lg transition-colors ${
                          isSelected 
                            ? 'bg-emerald-800/30 border-emerald-600 text-emerald-200' 
                            : 'bg-gray-800/50 border-emerald-800/30 text-emerald-200 hover:bg-emerald-800/20'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <div className={`w-4 h-4 border rounded-sm ${
                            post.poll!.allowMultipleChoice ? 'rounded-sm' : 'rounded-full'
                          } ${isSelected ? 'bg-emerald-600 border-emerald-600' : 'border-emerald-600'}`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                              </svg>
                            )}
                          </div>
                          <span className="text-sm">{option.text}</span>
                        </div>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bouton de soumission pour choix multiples */}
            {!hasVoted && post.poll.allowMultipleChoice && selectedPollOptions.length > 0 && (
              <button
                onClick={submitPollVote}
                disabled={isVoting}
                className="mt-3 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
              >
                {isVoting ? 'Vote en cours...' : 'Voter'}
              </button>
            )}

            {/* Bouton de soumission pour choix unique */}
            {!hasVoted && !post.poll.allowMultipleChoice && selectedPollOptions.length > 0 && (
              <button
                onClick={submitPollVote}
                disabled={isVoting}
                className="mt-3 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
              >
                {isVoting ? 'Vote en cours...' : 'Voter'}
              </button>
            )}

            {/* Informations du sondage */}
            <div className="mt-3 flex items-center justify-between text-xs text-emerald-300/60">
              <span>
                {(() => {
                  const totalVotes = post.poll!.options.reduce((sum, opt) => sum + opt.votes.length, 0);
                  return `${totalVotes} vote${totalVotes !== 1 ? 's' : ''}`;
                })()}{' • '}
                {post.poll.allowMultipleChoice ? ' Choix multiples' : ' Choix unique'}
              </span>
              <span>
                {new Date(post.poll.expiresAt) > new Date() 
                  ? `Expire le ${new Date(post.poll.expiresAt).toLocaleDateString('fr-FR')}`
                  : 'Expiré'
                }
              </span>
            </div>
          </div>
        )}

        {/* Médias */}
        <MediaDisplay
          mediaUrls={post.mediaUrls || []}
          mediaTypes={post.mediaTypes}
          onMediaClick={setSelectedImageIndex}
          autoPlay={false}
          showControls={true}
        />

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-emerald-800/30 mt-4">
          <div className="flex items-center space-x-6">
            <button
              onClick={handleLike}
              disabled={isLiking}
              className={`flex items-center space-x-2 transition-colors cosmic-hover-effect ${
                isLiked 
                  ? 'text-red-400 hover:text-red-300' 
                  : 'text-emerald-300/80 hover:text-red-400'
              }`}
            >
              <svg className="w-5 h-5" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span className="text-sm">{likesCount}</span>
            </button>

            <button 
              className="flex items-center space-x-2 text-emerald-300/80 hover:text-cyan-300 transition-colors cosmic-hover-effect"
              onClick={() => onPostClick?.(post)}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-sm">{post.comments.length}</span>
            </button>

            <button 
              className="flex items-center space-x-2 text-emerald-300/80 hover:text-emerald-300 transition-colors cosmic-hover-effect"
              onClick={handleShare}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
              <span className="text-sm">{sharesCount}</span>
            </button>

            {/* Bouton Space Discussion */}
            <button 
              className="flex items-center space-x-2 text-purple-300/80 hover:text-purple-300 transition-colors cosmic-hover-effect"
              onClick={() => setShowCreateSpaceDropdown(!showCreateSpaceDropdown)}
              title="Créer une discussion vocale"
            >
              <span className="text-base">🎙️</span>
              <span className="text-sm">Discuter</span>
            </button>
          </div>

          <div className="flex items-center space-x-2 text-sm text-emerald-300/60">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span>{formatViewCount(post.views || 0)} vues</span>
          </div>
        </div>

        {/* Menu déroulant Space intégré dans le postcard */}
        <SpaceDropdownMenu
          postId={post.id}
          postTitle={post.content.slice(0, 50) + (post.content.length > 50 ? '...' : '')}
          onCreateSpace={handleCreateSpace}
          onClose={() => setShowCreateSpaceDropdown(false)}
          isOpen={showCreateSpaceDropdown}
        />

        {/* Widget Space s'il y en a un d'actif */}
        {!spacesLoading && spaces.length > 0 && (
          <div>
            {spaces.map(space => (
              <SpaceCardWidget
                key={space.id}
                space={space}
                onJoin={() => handleJoinSpace(space.id)}
                onView={() => navigate(`/spaces/${space.id}`)}
                isUserInSpace={isUserInSpace(space.id)}
              />
            ))}
          </div>
        )}
      </div>
      </SwipeablePostCard>
      
      <ImageModal />
    </>
  );
};

export default PostCard;
